import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * A custom element that manages a quick order form for adding products by SKU.
 *
 * @typedef {object} QuickOrderFormRefs
 * @property {HTMLInputElement} skuInput - The SKU input field.
 * @property {HTMLInputElement} quantityInput - The quantity input field.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} resultsContainer - The results table container.
 * @property {HTMLElement} resultsTableBody - The results table body.
 * @property {HTMLElement} resultsEmpty - The empty state element.
 * @property {HTMLButtonElement} clearResultsButton - The clear results button.
 * @property {HTMLInputElement} fileInput - The CSV file input.
 * @property {HTMLButtonElement} fileProcessButton - The CSV process button.
 * @property {HTMLElement} csvProgress - The CSV progress container.
 * @property {HTMLElement} csvProgressFill - The CSV progress fill bar.
 * @property {HTMLElement} csvProgressText - The CSV progress text.
 * @property {HTMLElement} dropZone - The file drop zone label.
 *
 * @extends Component<QuickOrderFormRefs>
 */
class QuickOrderFormComponent extends Component {
  requiredRefs = ['skuInput', 'quantityInput', 'submitButton', 'resultsContainer', 'resultsTableBody', 'resultsEmpty', 'clearResultsButton', 'fileInput', 'fileProcessButton', 'csvProgress', 'csvProgressFill', 'csvProgressText', 'dropZone'];

  #abortController = new AbortController();
  /** Set when predictive search returns more than one product (lookup must fail, no fallback). */
  #predictiveMultipleProducts = false;
  #successMessage = 'Added to Cart';
  #buttonMessageLong = 'Added to Cart';
  #buttonMessageLongLoading = 'Adding...';
  #buttonMessageShort = 'Add';
  #buttonMessageShortLoading = '...';
  #fileUploaderMessage = 'Choose CSV/Excel File';
  #buttonProcessMessage = 'Process File';
  #buttonProgressMessage = 'Processing...';
  #errorInvalidInput = "Invalid Input";
  #errorSkuRequired = "SKU required";
  #errorInvalidQuantity = "Invalid Quantity";
  #errorUnknownProduct = "Unknown Product";
  #errorOutOfStock = "Out of Stock";
  #errorZeroPrice = "Item cannot be added to the order";
  #errorFailedToAtc = "Failed to add to cart";
  #errorNetworkError = "Network Error";
  #errorResultsTableHeaderSku = "Invalid";
  #errorResultsTableInvalidRow = "Invalid File Row";
  #errorResultsTableInvalidSkuQty = "Invalid SKU or quantity";
  #errorResultsTableFileError = "File Error";
  #errorResultsTableProcessFile = "Failed to process file";

  #errorParsingEmpty = "CSV file is empty";
  #errorParsingInvalidCsv = "Invalid CSV format. First line must be: sku,quantity.";
  #errorParsingEmptyExcel = "Excel file contains no sheets";
  #errorParsingFailedParsingExcel = "Could not parse the Excel file. Please check the format.";
  #errorParsingFailedText = "Failed to read file as text";
  #errorParsingFailedArrayBuffer = "Failed to read file as ArrayBuffer";

  #progressProcessing = "Processing:";
  #progressOf = "of";
  #progressCompleted = "Completed processing";
  #progressItems = "item(s)";
  #progressReadingFile = "Reading file...";
  #errorProgressNoItems = "No items found in file";
  #errorProgressProcessingFile = "Error processing file";

  constructor() {
    super();
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    if (window.quickOrderTranslations) {
      this.#successMessage = window.quickOrderTranslations.successMessage;
      this.#buttonMessageLong = window.quickOrderTranslations.submitFull;
      this.#buttonMessageLongLoading = window.quickOrderTranslations.submitFullLoading;
      this.#buttonMessageShort = window.quickOrderTranslations.submitShort;
      this.#buttonMessageShortLoading = window.quickOrderTranslations.submitShortLoading;
      this.#fileUploaderMessage = window.quickOrderTranslations.fileUploader;
      this.#buttonProcessMessage = window.quickOrderTranslations.processButton;
      this.#buttonProgressMessage = window.quickOrderTranslations.progressButton;

      this.#errorInvalidInput = window.quickOrderTranslations.errorInvalidInput;
      this.#errorSkuRequired = window.quickOrderTranslations.errorSkuRequired;
      this.#errorInvalidQuantity = window.quickOrderTranslations.errorInvalidQuantity;
      this.#errorUnknownProduct = window.quickOrderTranslations.errorUnknownProduct;
      this.#errorOutOfStock = window.quickOrderTranslations.errorOutOfStock;
      this.#errorZeroPrice = window.quickOrderTranslations.errorZeroPrice;
      this.#errorFailedToAtc = window.quickOrderTranslations.errorFailedToAtc;
      this.#errorNetworkError = window.quickOrderTranslations.errorNetworkError;

      this.#errorResultsTableHeaderSku = window.quickOrderTranslations.errorResultsTableHeaderSku;
      this.#errorResultsTableInvalidRow = window.quickOrderTranslations.errorResultsTableInvalidRow;
      this.#errorResultsTableInvalidSkuQty = window.quickOrderTranslations.errorResultsTableInvalidSkuQty;
      this.#errorResultsTableFileError = window.quickOrderTranslations.errorResultsTableFileError;
      this.#errorResultsTableProcessFile = window.quickOrderTranslations.errorResultsTableProcessFile;

      this.#errorParsingEmpty = window.quickOrderTranslations.errorParsingEmpty;
      this.#errorParsingInvalidCsv = window.quickOrderTranslations.errorParsingInvalidCsv;
      this.#errorParsingEmptyExcel = window.quickOrderTranslations.errorParsingEmptyExcel;
      this.#errorParsingFailedParsingExcel = window.quickOrderTranslations.errorParsingFailedParsingExcel;
      this.#errorParsingFailedText = window.quickOrderTranslations.errorParsingFailedText;
      this.#errorParsingFailedArrayBuffer = window.quickOrderTranslations.errorParsingFailedArrayBuffer;

      this.#progressProcessing = window.quickOrderTranslations.progressProcessing;
      this.#progressOf = window.quickOrderTranslations.progressOf;
      this.#progressCompleted = window.quickOrderTranslations.progressCompleted;
      this.#progressItems = window.quickOrderTranslations.progressItems;
      this.#progressReadingFile = window.quickOrderTranslations.progressReadingFile;
      this.#errorProgressNoItems = window.quickOrderTranslations.errorProgressNoItems;
      this.#errorProgressProcessingFile = window.quickOrderTranslations.errorProgressProcessingFile;
    }

    this.addEventListener('submit', this.handleSubmit);
    this.refs.clearResultsButton.addEventListener('click', this.handleClearResults);
    this.refs.fileInput.addEventListener('change', this.handleFileInputChange);
    this.refs.fileProcessButton.addEventListener('click', this.handleFileProcess);

    if (this.refs.dropZone) {
      this.refs.dropZone.addEventListener('dragenter', this.handleDragEnter);
      this.refs.dropZone.addEventListener('dragover', this.handleDragOver);
      this.refs.dropZone.addEventListener('dragleave', this.handleDragLeave);
      this.refs.dropZone.addEventListener('drop', this.handleDrop);
    }

    this.updateResultsVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener('submit', this.handleSubmit);
    this.refs.clearResultsButton.removeEventListener('click', this.handleClearResults);
    this.refs.fileInput.removeEventListener('change', this.handleFileInputChange);
    this.refs.fileProcessButton.removeEventListener('click', this.handleFileProcess);

    if (this.refs.dropZone) {
      this.refs.dropZone.removeEventListener('dragenter', this.handleDragEnter);
      this.refs.dropZone.removeEventListener('dragover', this.handleDragOver);
      this.refs.dropZone.removeEventListener('dragleave', this.handleDragLeave);
      this.refs.dropZone.removeEventListener('drop', this.handleDrop);
    }
  }

  /**
   * Handles the form submission
   * @param {Event} event - The submit event
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    const sku = this.refs.skuInput.value.trim();
    const quantity = parseInt(this.refs.quantityInput.value) || 1;

    if (!sku) {
      this.addResult('', this.#errorInvalidInput, quantity, 'error', this.#errorSkuRequired);
      return;
    }
    if (quantity < 1) {
      this.addResult(sku, this.#errorInvalidInput, quantity, 'error', this.#errorInvalidQuantity);
      return;
    }

    this.setLoading(true);

    try {
      const variant = await this.findVariantBySku(sku);

      if (!variant) {
        this.addResult(sku, this.#errorUnknownProduct, quantity, 'error', this.#errorUnknownProduct);
        return;
      }

      if (!variant.available) {
        this.addResult(sku, variant.title, quantity, 'error', this.#errorOutOfStock);
        return;
      }

      if (!variant.price || variant.price <= 0) {
        this.addResult(sku, variant.title, quantity, 'error', this.#errorZeroPrice);
        return;
      }

      try {
        await this.addToCart(variant, quantity);

        this.refs.skuInput.value = '';
        this.refs.quantityInput.value = '1';
        this.animateSuccess();
        this.animateFlyToCart(variant);
        this.addResult(sku, variant.title, quantity, 'success');

      } catch (cartError) {
        this.addResult(sku, variant.title, quantity, 'error', cartError.message);
      }

    } catch (searchError) {
      let errorMessage = this.#errorFailedToAtc;
      if (searchError.message.includes('Search failed')) {
        errorMessage = this.#errorUnknownProduct;
      } else if (searchError.message.includes('Failed to fetch')) {
        errorMessage = this.#errorNetworkError;
      }
      this.addResult(sku, this.#errorUnknownProduct, quantity, 'error', errorMessage);
    } finally {
      this.setLoading(false);
      setTimeout(() => {
        this.refs.skuInput.focus();
      }, 100);
    }
  };

  /**
   * --- RESTORED LOGIC ---
   * Finds a variant by SKU using Shopify's predictive search
   * @param {string} sku - The SKU to search for
   * @returns {Promise<any|null>} The variant object or null if not found
   */
  async findVariantBySku(sku) {
    this.#predictiveMultipleProducts = false;
    try {
      const result = await this.fetchVariantIdBySku(sku);
      if (result) {
        return result;
      }

      if (this.#predictiveMultipleProducts) {
        return null;
      }

      return await this.fallbackSearchBySku(sku);

    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  }

  /**
   * --- RESTORED LOGIC ---
   * Fetches variant via predictive search. If more than one product is returned, returns null (ambiguous).
   * @param {string} sku - The SKU or search phrase
   * @returns {Promise<any|null>} The variant object or null if not found
   */
  async fetchVariantIdBySku(sku) {
    try {
      const searchUrl = `${Theme.routes.predictive_search_url}.json?q=${encodeURIComponent(sku)}&resources[type]=product&resources[options][fields]=variants.sku,body`;

      const response = await this.fetchWithRetry(searchUrl, {
        signal: this.#abortController.signal,
      });


      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      if (products.length > 1) {
        this.#predictiveMultipleProducts = true;
        return null;
      }

      const predictiveResultCount = products.length;

      for (const product of products) {
        if (!product.variants || !Array.isArray(product.variants)) continue;

        // Look for exact SKU match in predictive search results first (fastest)
        for (const variant of product.variants) {
          if (variant.sku && variant.sku.toLowerCase().trim() === sku.toLowerCase().trim()) {
            // We found a direct match, but predictive search doesn't give full variant data.
            // Fetch the full product to get all details.
            const fullProduct = await this.getProductData(product.handle);
            const resolved = this.findMatchingVariant(fullProduct, sku, {
              predictiveResultCount,
            });
            if (resolved) return resolved;
          }
        }
      }

      // If no exact match in the returned SKU data, try a broader search on the results
      for (const product of products) {
        const fullProduct = await this.getProductData(product.handle);
        if (!fullProduct) continue;

        const result = this.findMatchingVariant(fullProduct, sku, {
          predictiveResultCount,
          trustPredictiveListing: true,
        });

        if (result) {
          return result;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * --- RESTORED LOGIC ---
   * Finds a matching variant by SKU, handle, or text that appears on the product (body, title, tags, options, etc.).
   * @param {any} product - The product data from Shopify
   * @param {string} sku - The SKU or search phrase
   * @param {{ predictiveResultCount?: number, trustPredictiveListing?: boolean, exactOnly?: boolean }} [options]
   * @returns {any|null} The variant object or null if not found
   */
  findMatchingVariant(product, sku, options = {}) {
    if (!product || !product.variants || !Array.isArray(product.variants)) {
      return null;
    }

    const skuLower = sku.toLowerCase().trim();
    const queryText = QuickOrderFormComponent.#normalizeSearchText(skuLower);
    const { predictiveResultCount = null, trustPredictiveListing = false, exactOnly = false } = options;

    // Case 1: Exakte SKU-Übereinstimmung (bevorzugt)
    for (const variant of product.variants) {
      if (variant.sku && variant.sku.toLowerCase().trim() === skuLower) {
        return {
          ...variant,
          title: `${product.title}${variant.title !== 'Default Title' ? ` - ${variant.title}` : ''}`,
          featured_image: variant.featured_image || product.featured_image
        };
      }
    }

    // Case 2: Single-Variant-Produkt (Produkt-SKU oder exakter Handle-Match)
    if (product.variants.length === 1) {
      const variant = product.variants[0];

      // Produkt-SKU checken
      if (product.sku && product.sku.toLowerCase().trim() === skuLower) {
        return {
          ...variant,
          sku: product.sku,
          title: product.title,
          featured_image: variant.featured_image || product.featured_image
        };
      }

      // Handle-Match, wenn Variant keine SKU hat
      if (!variant.sku || variant.sku.trim() === '') {
        if (
            product.handle &&
            product.handle.toLowerCase().replace(/-/g, '') === skuLower.replace(/-/g, '')
        ) {
          return {
            ...variant,
            sku: variant.sku || product.sku || sku,
            title: product.title,
            featured_image: variant.featured_image || product.featured_image
          };
        }
      }
    }

    // When resolving a batch we scan several products per SKU, so only exact matches
    // (Case 1/2) are trustworthy — fuzzy text matching could assign a SKU to the wrong product.
    if (exactOnly) {
      return null;
    }

    // Case 3: Phrase appears somewhere Shopify typically indexes (body + title + tags + options, etc.).
    // Predictive search can match indexed text that does not exactly match stripped body_html alone.
    const haystack = QuickOrderFormComponent.#buildProductMatchHaystack(product);
    if (queryText.length > 0 && haystack.includes(queryText)) {
      return QuickOrderFormComponent.#pickVariantForTextMatch(product);
    }

    // Case 4: Only one predictive hit — trust the API when local JSON cannot reproduce the index match.
    if (
      trustPredictiveListing &&
      predictiveResultCount === 1 &&
      queryText.length > 0
    ) {
      return QuickOrderFormComponent.#pickVariantForTextMatch(product);
    }

    return null;
  }

  /**
   * Plain-text fields from /products/*.js combined for substring search (aligned with common search indexing).
   * @param {any} product
   * @returns {string}
   */
  static #buildProductMatchHaystack(product) {
    const parts = [];
    if (product.title) parts.push(product.title);
    if (product.handle) parts.push(product.handle.replace(/-/g, ' '));
    if (typeof product.body_html === 'string' && product.body_html) {
      parts.push(QuickOrderFormComponent.#stripHtml(product.body_html));
    }
    if (typeof product.description === 'string' && product.description) {
      parts.push(
        product.description.includes('<')
          ? QuickOrderFormComponent.#stripHtml(product.description)
          : product.description
      );
    }
    if (typeof product.product_type === 'string' && product.product_type) {
      parts.push(product.product_type);
    }
    if (typeof product.vendor === 'string' && product.vendor) {
      parts.push(product.vendor);
    }
    if (typeof product.tags === 'string' && product.tags) {
      parts.push(product.tags.replace(/,/g, ' '));
    }

    for (const v of product.variants || []) {
      if (v.sku) parts.push(String(v.sku));
      if (v.title && v.title !== 'Default Title') parts.push(String(v.title));
      if (v.option1) parts.push(String(v.option1));
      if (v.option2) parts.push(String(v.option2));
      if (v.option3) parts.push(String(v.option3));
    }

    return QuickOrderFormComponent.#normalizeSearchText(parts.join(' '));
  }

  /**
   * @param {any} product
   * @returns {any}
   */
  static #pickVariantForTextMatch(product) {
    const variant =
      product.variants.find((/** @type {any} */ v) => v.available) ||
      product.variants[0];
    return {
      ...variant,
      title: `${product.title}${variant.title !== 'Default Title' ? ` - ${variant.title}` : ''}`,
      featured_image: variant.featured_image || product.featured_image
    };
  }

  /**
   * @param {string} html
   * @returns {string}
   */
  static #stripHtml(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  static #normalizeSearchText(text) {
    return text.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /**
   * --- RESTORED LOGIC ---
   * Fallback search method when predictive search doesn't find results
   * @param {string} sku - The SKU to search for
   * @returns {Promise<any|null>} The variant object or null if not found
   */
  async fallbackSearchBySku(sku) {
    try {
      const cleanSku = sku.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
      const directProduct = await this.getProductData(cleanSku);

      if (directProduct) {
        const result = this.findMatchingVariant(directProduct, sku);
        if (result) {
          return result;
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  async getProductData(productIdentifier) {
    try {
      const response = await this.fetchWithRetry(`/products/${productIdentifier}.js`, { signal: this.#abortController.signal });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  /**
   * Wraps fetch with retry/backoff on HTTP 429 (rate limiting).
   * Honors the `Retry-After` header when present, otherwise uses exponential backoff.
   * @param {string} url - The URL to fetch.
   * @param {RequestInit} [options] - Fetch options.
   * @param {number} [maxRetries] - Maximum number of retries on 429.
   * @returns {Promise<Response>} The fetch response.
   */
  async fetchWithRetry(url, options = {}, maxRetries = 5) {
    let attempt = 0;
    while (true) {
      const response = await fetch(url, { ...options, signal: this.#abortController.signal });

      if (response.status !== 429 || attempt >= maxRetries) {
        return response;
      }

      const retryAfterHeader = parseFloat(response.headers.get('Retry-After') || '');
      const retryAfterMs = Number.isFinite(retryAfterHeader)
        ? retryAfterHeader * 1000
        : Math.min(1000 * 2 ** attempt, 10000);

      await this.delay(retryAfterMs);
      attempt++;
    }
  }

  /**
   * @param {number} ms - Milliseconds to wait.
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * --- CORRECTED METHOD ---
   * Adds a product variant to the cart and handles API responses correctly.
   * @param {object} variant - The full variant object to add.
   * @param {number} quantity - The quantity to add.
   */
  async addToCart(variant, quantity) {
    try {
      const response = await this.fetchWithRetry(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: quantity }],
          sections: this.getCartSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 422) {
          this.dispatchEvent(new CartAddEvent({}, variant.id.toString(), {
            source: 'quick-order-form',
            itemCount: quantity,
          }))
        }

        if (responseData.sections) {
          this.updateCartSections(responseData.sections);
        }

        this.refs.skuInput.value = '';
        this.refs.quantityInput.value = '1';

        throw new Error(responseData.description || responseData.message || this.#errorFailedToAtc);
      }

      this.updateCartSections(responseData.sections);

      this.dispatchEvent(new CartAddEvent({}, variant.id.toString(), {
        source: 'quick-order-form',
        itemCount: quantity,
      }));
    } catch (error) {
      this.dispatchEvent(new CartErrorEvent(this.id, error.message, '', {}));
      throw error;
    }
  }

  /**
   * Resolves a batch of SKUs to variants using a single predictive search (`OR` query),
   * then a per-product lookup for full variant data (variant IDs are not returned by
   * predictive search). SKUs the batch misses — e.g. when a chunk yields more than the
   * 10 results predictive search returns — fall back to an individual lookup so nothing
   * is silently dropped.
   * @param {string[]} skus - The SKUs to resolve.
   * @returns {Promise<Map<string, any>>} Map of SKU -> variant (absent if unresolved).
   */
  async resolveVariantsForBatch(skus) {
    const resolved = new Map();

    const candidates = await this.fetchPredictiveProducts(skus.join(' OR '));

    const fullProducts = [];
    for (const candidate of candidates) {
      if (!candidate.handle) continue;
      const fullProduct = await this.getProductData(candidate.handle);
      if (fullProduct) fullProducts.push(fullProduct);
    }

    for (const sku of skus) {
      for (const product of fullProducts) {
        const variant = this.findMatchingVariant(product, sku, { exactOnly: true });
        if (variant) {
          resolved.set(sku, variant);
          break;
        }
      }
    }

    // Fall back to the single-SKU lookup for anything the batch could not resolve.
    for (const sku of skus) {
      if (resolved.has(sku)) continue;
      try {
        const variant = await this.findVariantBySku(sku);
        if (variant) resolved.set(sku, variant);
      } catch (error) {
        // Leave unresolved — reported as an unknown product by the caller.
      }
    }

    return resolved;
  }

  /**
   * Runs a single predictive search and returns the product candidates.
   * @param {string} query - The search query (SKUs joined with ` OR `).
   * @returns {Promise<any[]>} The predictive-search product results.
   */
  async fetchPredictiveProducts(query) {
    const searchUrl = `${Theme.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[options][fields]=variants.sku,body`;
    const response = await this.fetchWithRetry(searchUrl, { signal: this.#abortController.signal });
    if (!response.ok) {
      throw new Error(`Search failed: ${response.status}`);
    }
    const data = await response.json();
    return data.resources?.results?.products || [];
  }

  /**
   * Adds all resolved items to the cart in a single bulk request. Because a bulk
   * add is atomic (one bad line fails the whole request), it falls back to sequential
   * per-item adds so valid rows still get added and failures are attributed per SKU.
   * @param {{variant: any, quantity: number, sku: string}[]} toAdd - The items to add.
   */
  async addResolvedItemsToCart(toAdd) {
    const before = await this.getCartQuantities();
    try {
      await this.addToCartBulk(toAdd);
      this.animateFlyToCart(toAdd[0].variant);
      for (const item of toAdd) {
        this.addResult(item.sku, item.variant.title, item.quantity, 'success');
      }
    } catch (bulkError) {
      // A failed bulk add (e.g. 422 for one line exceeding stock) can still commit the
      // other lines, so only add what did not land — otherwise quantities get doubled.
      const after = await this.getCartQuantities();
      const alreadyAdded = new Map();
      for (const [id, qty] of after) alreadyAdded.set(id, qty - (before.get(id) || 0));

      for (let i = 0; i < toAdd.length; i++) {
        const item = toAdd[i];
        const landed = Math.min(Math.max(alreadyAdded.get(item.variant.id) || 0, 0), item.quantity);
        alreadyAdded.set(item.variant.id, (alreadyAdded.get(item.variant.id) || 0) - landed);
        const remaining = item.quantity - landed;
        if (remaining === 0) {
          this.addResult(item.sku, item.variant.title, item.quantity, 'success');
          continue;
        }
        try {
          await this.addToCart(item.variant, remaining);
          this.addResult(item.sku, item.variant.title, item.quantity, 'success');
        } catch (cartError) {
          this.addResult(item.sku, item.variant.title, item.quantity, 'error', cartError.message);
        }
        if (i < toAdd.length - 1) await this.delay(150);
      }
    }
  }

  /**
   * Returns the current cart quantities keyed by variant ID.
   * @returns {Promise<Map<number, number>>}
   */
  async getCartQuantities() {
    const response = await this.fetchWithRetry(`${Theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } });
    const cart = await response.json();
    const quantities = new Map();
    for (const line of cart.items) quantities.set(line.variant_id, (quantities.get(line.variant_id) || 0) + line.quantity);
    return quantities;
  }

  /**
   * Adds multiple variants to the cart in a single request.
   * @param {{variant: any, quantity: number}[]} toAdd - The items to add.
   */
  async addToCartBulk(toAdd) {
    const response = await this.fetchWithRetry(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        items: toAdd.map((item) => ({ id: item.variant.id, quantity: item.quantity })),
        sections: this.getCartSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      if (responseData.sections) this.updateCartSections(responseData.sections);
      throw new Error(responseData.description || responseData.message || this.#errorFailedToAtc);
    }

    this.updateCartSections(responseData.sections);

    const totalItems = toAdd.reduce((sum, item) => sum + item.quantity, 0);
    this.dispatchEvent(new CartAddEvent({}, toAdd[0].variant.id.toString(), {
      source: 'quick-order-form',
      itemCount: totalItems,
    }));
  }

  /**
   * --- CORRECTED METHOD ---
   * Updates cart sections from the JSON response.
   * @param {object} sections - The sections object from the cart API response.
   */
  updateCartSections(sections) {
    if (!sections) return;
    try {
      this.getCartSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id);
        const sectionHtml = sections[section.section];
        if (elementToReplace && sectionHtml) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, `#${section.id}`);
        }
      });

      const items = document.querySelectorAll('.cart-item');
      this.getCartSectionsToUpdate().forEach((section) => {
        const elementToUpdate = document.querySelector(section.selector);
        elementToUpdate?.classList.toggle('is-empty', items.length === 0);
      });

      document.querySelector('cart-items')?.classList.toggle('section-responsive-padding', items.length !== 0);
    } catch (e) {
      console.error('Failed to update cart sections:', e);
    }
  }

  /**
   * --- NEW HELPER METHOD ---
   * Gets the inner HTML of a section from a full HTML string.
   * @param {string} html - The full HTML content of the section.
   * @param {string} selector - The selector for the element to extract.
   * @returns {string} The inner HTML.
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  getCartSectionsToRender() {
    return [
      { id: 'main-cart-items', section: 'main-cart-items', selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: 'main-cart-footer', selector: '.js-contents' },
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
    ];
  }

  getCartSectionsToUpdate() {
    return [
      { id: 'cart-items', selector: 'cart-items' },
      { id: 'cart-drawer', selector: 'cart-drawer' },
      { id: 'main-cart-footer', selector: '#main-cart-footer' },
    ];
  }

  setLoading(loading) {
    this.refs.submitButton.disabled = loading;
    this.refs.skuInput.disabled = loading;
    this.refs.quantityInput.disabled = loading;
    const fullText = this.refs.submitButton.querySelector('.quick-order-text__full');
    const shortText = this.refs.submitButton.querySelector('.quick-order-text__short');
    if (fullText) fullText.textContent = loading ? this.#buttonMessageLongLoading : this.#buttonMessageLong;
    if (shortText) shortText.textContent = loading ? this.#buttonMessageShortLoading : this.#buttonMessageShort;
  }

  animateSuccess() {
    const button = this.refs.submitButton;
    button.classList.remove('qo-added');
    button.classList.add('qo-added');
    setTimeout(() => {
      button.classList.remove('qo-added');
    }, 2000);
  }

  animateFlyToCart(variant) {
    const { submitButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !submitButton) return;
    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-color', '#C8102E');
    const productImage = variant?.featured_image;
    if (productImage) {
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.style.setProperty('background-size', 'cover');
      flyToCartElement.style.setProperty('background-position', 'center');
    }
    flyToCartElement.source = submitButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  handleDragEnter = (event) => {
    event.preventDefault();
    this.refs.dropZone.classList.add('is-dragover');
  };
  handleDragOver = (event) => {
    event.preventDefault();
  };
  handleDragLeave = (event) => {
    event.preventDefault();
    this.refs.dropZone.classList.remove('is-dragover');
  };
  handleDrop = (event) => {
    event.preventDefault();
    this.refs.dropZone.classList.remove('is-dragover');
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.refs.fileInput.files = files;
      this.refs.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  addResult(sku, productName, quantity, status, errorMessage = '') {
    const row = document.createElement('tr');
    row.role = 'row';
    const statusText = status === 'success' ? this.#successMessage : errorMessage;
    row.innerHTML = `
      <td><span class="quick-order-sku">${this.escapeHtml(sku)}</span></td>
      <td><span class="quick-order-product">${this.escapeHtml(productName)}</span></td>
      <td><span class="quick-order-quantity">${quantity}</span></td>
      <td>
        <span class="quick-order-status quick-order-status--${status}">
          ${this.escapeHtml(statusText)}
        </span>
      </td>`;
    this.refs.resultsTableBody.insertBefore(row, this.refs.resultsTableBody.firstChild);
    this.updateResultsVisibility();
  }

  handleClearResults = () => {
    this.refs.resultsTableBody.innerHTML = '';
    this.updateResultsVisibility();
  };

  updateResultsVisibility() {
    const hasResults = this.refs.resultsTableBody.children.length > 0;
    this.refs.resultsContainer.classList.toggle('has-results', hasResults);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  handleFileInputChange = () => {
    const file = this.refs.fileInput.files?.[0];
    const label = this.querySelector('.quick-order-csv__main');
    this.refs.fileProcessButton.disabled = !file;
    if (label) {
      label.textContent = file ? file.name : this.#fileUploaderMessage;
    }
  };

  handleFileProcess = async () => {
    const file = this.refs.fileInput.files?.[0];
    if (!file) return;

    this.refs.fileProcessButton.disabled = true;
    this.refs.fileProcessButton.textContent = this.#buttonProgressMessage;
    this.showProgress();
    this.updateProgress(0, this.#progressReadingFile);

    try {
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      const items = isExcel ? await this.parseExcel(file) : this.parseCsv(await this.readFileAsText(file));
      if (items.length === 0) {
        this.updateProgress(100, this.#errorProgressNoItems);
        setTimeout(() => this.hideProgress(), 2000);
        return;
      }

      // Basic per-row validation first; only valid rows go through lookup/add.
      /** @type {{sku: string, quantity: number}[]} */
      const validRows = [];
      for (const item of items) {
        if (!item) continue;
        const { sku, quantity } = item;
        if (!sku || !quantity || quantity < 1) {
          this.addResult(sku || this.#errorResultsTableHeaderSku, this.#errorResultsTableInvalidRow, quantity || 0, 'error', this.#errorResultsTableInvalidSkuQty);
          continue;
        }
        validRows.push({ sku, quantity });
      }

      // Resolve SKUs to variants in batches — one predictive search per chunk via `OR`
      // (predictive search caps at 10 results, so keep chunks small for headroom).
      const BATCH_SIZE = 6;
      /** @type {{variant: any, quantity: number, sku: string}[]} */
      const toAdd = [];
      let processed = 0;

      for (let start = 0; start < validRows.length; start += BATCH_SIZE) {
        const chunk = validRows.slice(start, start + BATCH_SIZE);
        this.updateProgress(
          Math.round((processed / validRows.length) * 90),
          `${this.#progressProcessing} ${processed + 1} ${this.#progressOf} ${validRows.length}`
        );

        let resolved;
        try {
          resolved = await this.resolveVariantsForBatch(chunk.map((row) => row.sku));
        } catch (searchError) {
          resolved = new Map();
        }

        for (const row of chunk) {
          const variant = resolved.get(row.sku);
          if (!variant) {
            this.addResult(row.sku, this.#errorUnknownProduct, row.quantity, 'error', this.#errorUnknownProduct);
          } else if (!variant.available) {
            this.addResult(row.sku, variant.title, row.quantity, 'error', this.#errorOutOfStock);
          } else if (!variant.price || variant.price <= 0) {
            this.addResult(row.sku, variant.title, row.quantity, 'error', this.#errorZeroPrice);
          } else {
            toAdd.push({ variant, quantity: row.quantity, sku: row.sku });
          }
        }

        processed += chunk.length;

        // Throttle between chunks to stay under Shopify's storefront rate limits.
        if (start + BATCH_SIZE < validRows.length) {
          await this.delay(150);
        }
      }

      // Add everything in a single bulk cart request (falls back to per-item on failure).
      if (toAdd.length > 0) {
        this.updateProgress(95, this.#buttonProgressMessage);
        await this.addResolvedItemsToCart(toAdd);
      }

      this.updateProgress(100, `${this.#progressCompleted} ${items.length} ${this.#progressItems}`);
      this.refs.fileInput.value = '';
      const label = this.querySelector('.quick-order-csv__main');
      if (label) label.textContent = this.#fileUploaderMessage;
    } catch (error) {
      this.addResult('', this.#errorResultsTableFileError, 0, 'error', error.message || this.#errorResultsTableProcessFile);
      this.updateProgress(100, this.#errorProgressProcessingFile);
    } finally {
      this.refs.fileProcessButton.disabled = true;
      this.refs.fileProcessButton.textContent = this.#buttonProcessMessage;
      setTimeout(() => this.hideProgress(), 2000);
    }
  };

  showProgress() { this.refs.csvProgress.style.display = 'block'; }
  hideProgress() {
    this.refs.csvProgress.style.display = 'none';
    this.refs.csvProgressFill.style.width = '0%';
    this.refs.csvProgressText.textContent = '';
  }

  updateProgress(percent, text) {
    this.refs.csvProgressFill.style.width = `${Math.min(100, Math.max(0, percent))}%`;
    this.refs.csvProgressText.textContent = text;
  }

  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error(this.#errorParsingFailedText));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  parseCsv(text) {
    const lines = text.trim().split('\n');
    if (lines.length === 0 || !lines[0]) throw new Error(this.#errorParsingEmpty);

    const headerParts = lines[0].trim().toLowerCase().split(/[,;]/).map(p => p.trim().replace(/['"]/g, ''));
    if (headerParts.length !== 2 || headerParts[0] !== 'sku' || headerParts[1] !== 'quantity') throw new Error(this.#errorParsingInvalidCsv);

    return lines.slice(1).map(line => {
      const parts = line.trim().split(/[,;]/).map(p => p.trim());
      if (!parts[0]) {
        return null;
      }
      const sku = parts[0].replace(/['"]/g, '');
      const quantity = parseInt(parts[1], 10);
      return { sku, quantity };
    }).filter(Boolean);
  }

  async parseExcel(file) {
    try {
      const data = await this.readFileAsArrayBuffer(file);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error(this.#errorParsingEmptyExcel);

      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });

      return jsonData.map(row => {
        const sku = row.sku || row.SKU;
        const rawQuantity = row.quantity || row.QUANTITY;

        if (typeof sku === 'string' && String(sku).trim()) {
          const quantity = parseInt(rawQuantity, 10);
          return { sku: String(sku).trim(), quantity };
        }
        return null;
      }).filter(Boolean);
    } catch (error) {
      throw new Error(this.#errorParsingFailedParsingExcel);
    }
  }

  readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => e.target?.result instanceof ArrayBuffer ? resolve(e.target.result) : reject(new Error(this.#errorParsingFailedArrayBuffer));
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }
}

if (!customElements.get('quick-order-form-component')) {
  customElements.define('quick-order-form-component', QuickOrderFormComponent);
}


